/**
 * A barra de nível do painel: o `XPProgressBar` de
 * `lib/features/xp/presentation/widgets/xp_progress_bar.dart`, com o
 * `_XpProgressSection` de `dashboard_screen.dart:1078` em volta.
 *
 * Mesmas medidas: cartão de 16 por 14, selo de nível com o degradê rosa-ouro,
 * a conta em cima e o que falta embaixo, trilho de 10 dentro de uma faixa de
 * 14 e a bolinha de 14 andando com o preenchimento.
 *
 * O que o navegador obrigou a mudar:
 *
 *  - O `Positioned.fill` do véu branco e o fio dourado do topo estão dentro do
 *    `padding` do `Container` no Flutter — o `Stack` é filho do `Padding`, não
 *    da borda. Então o véu começa a 14 e 16 da beirada, e o fio, que é
 *    `left: 14` dentro disso, começa a 30. É o que aparece na tela lá, e é o
 *    que está aqui.
 *
 *  - O `LayoutBuilder` some: a largura do trilho é a do elemento, e a conta do
 *    `fillW` e do `left: (fillW - 7).clamp(...)` vira `calc` e `clamp` em cima
 *    de uma porcentagem. Não precisa medir nada para desenhar.
 *
 *  - Só o preenchimento anda devagar, como lá: o `AnimatedContainer` de 900ms
 *    é só dele, e a bolinha do Flutter pula junto com o `LayoutBuilder`.
 *
 *  - O `role="progressbar"` não tem gêmeo: o widget de lá não fala com o leitor
 *    de tela. Aqui um trilho pintado sem número dito é um trilho que não existe
 *    para quem não enxerga.
 *
 *  - A conta de 900ms para quando a pessoa pede menos movimento. Lá ela não
 *    para — o `Duration` não passa pelo `AppMotion.orZero` —, mas é a regra da
 *    casa e uma barra que anda sozinha é movimento que ninguém pediu.
 *
 *  - A sequência de dias não tem gêmeo no `XPProgressBar`, e não por desenho:
 *    o `addXp` de lá (`xp_service.dart:29`) escreve `streak: 1` só no ramo que
 *    cria o documento e nunca mais encosta no campo, então o número é uma
 *    constante e não haveria o que mostrar. Aqui quem soma é o servidor
 *    (`server/src/xp.js`), pela regra de dias do jardim, e um número que anda
 *    merece aparecer. O selo é o do cartão do jardim, que já conta a mesma
 *    coisa nesta casa.
 *
 *  - E o "+25 XP" que passa por cima quando o ponto chega. Não tem gêmeo
 *    nenhum: o Flutter só empurra a barra e toca um `level_up.mp3` que é 417
 *    bytes de zeros. A conta de quanto chegou é do `usarFesta` (ver
 *    `xp/festa.ts`, que explica por que ela é do cliente); aqui só se desenha.
 */

import { useEffect } from 'react';
import {
  COLECOES,
  PONTOS_POR_NIVEL,
  fatiaCheia,
  garantirProgresso,
  pontosNoNivel,
  pontosQueFaltam,
  progressoDeMapa,
  traduzir,
  usarDocumento,
  usarFesta,
  usarSessao,
} from '@nosso-sempre/shared';
import { Icone } from './Icone';
import './barraDeXp.css';

export function BarraDeXp() {
  const { quem } = usarSessao();
  const doc = usarDocumento<Record<string, unknown>>(COLECOES.xp, quem ?? '');

  /* Antes do `return null` de propósito: gancho não pode nascer depois de um
     `return`, e o `usarFesta` logo abaixo é um. É também o que o gêmeo do
     celular já fazia. */
  const p = quem && doc ? progressoDeMapa(quem, doc) : null;
  const festa = usarFesta(p);

  /* O `initializeProgress` que o login do Flutter faz. Quem chama é esta barra
     porque é a única coisa na casa que lê este documento: sem isso ela nunca
     apareceria para quem entrou depois do Flutter. */
  useEffect(() => {
    if (!quem) return;
    void garantirProgresso(quem);
  }, [quem]);

  /* O `SizedBox.shrink()` de lá quando não há quem ou não há documento. */
  if (!p) return null;

  const feitos = pontosNoNivel(p.total);
  const faltam = pontosQueFaltam(p.total);

  return (
    <div className="xp" style={{ ['--fatia' as string]: String(fatiaCheia(p.total)) }}>
      <p className="xp-linha">
        <span className="xp-selo">
          <Icone nome="raio" tamanho={13} preenchido />
          LEVEL {p.nivel}
        </span>

        {/* A sequência de dias, quando há uma. O gêmeo é o selo do cartão do
            jardim, que conta a mesma coisa com a mesma chama; sem `traduzir`
            pela mesma razão que lá — a frase já nasce em inglês na tela, e o
            dicionário só guarda o que está em português. */}
        {p.sequencia > 0 ? (
          <span className="xp-fogo">
            <Icone nome="chama" tamanho={12} />
            {p.sequencia} day streak
          </span>
        ) : null}

        {/* O aviso do ponto que acabou de chegar. O `key` é o total: ele só
            cresce, então dois prêmios seguidos dentro dos quatro segundos
            trocam o elemento e a animação recomeça em vez de continuar de onde
            a anterior parou. `aria-hidden` porque quem lê a tela ouve o aviso
            de baixo, que é fixo — uma região viva que nasce junto com o texto
            às vezes não é anunciada. */}
        {festa.ganhou > 0 ? (
          <span className="xp-festa" key={p.total} aria-hidden="true">
            {festa.subiu ? <span className="xp-subiu">{traduzir('Subiu de nível!')}</span> : null}
            <span className="xp-ganho">{`+${festa.ganhou} XP`}</span>
          </span>
        ) : null}

        <span className="xp-contas">
          <span className="xp-conta">{`${feitos} / ${PONTOS_POR_NIVEL} XP`}</span>
          <span className="xp-falta">
            {traduzir('{faltam} XP para o nível {v}', { faltam, v: p.nivel + 1 })}
          </span>
        </span>
      </p>

      {/* Fixa no documento e vazia quando não há festa: o leitor de tela se
          inscreve nela uma vez e anuncia cada troca de texto. Se ela só
          aparecesse junto com o aviso, metade dos leitores não diria nada. */}
      <p className="so-leitor" role="status">
        {festa.ganhou > 0
          ? `${festa.subiu ? `${traduzir('Subiu de nível!')} ` : ''}+${festa.ganhou} XP`
          : ''}
      </p>

      <div
        className="xp-trilho"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={PONTOS_POR_NIVEL}
        aria-valuenow={feitos}
        aria-valuetext={traduzir('{feitos} de {pontos_por_nivel} pontos do nível {nivel}', {
          feitos,
          pontos_por_nivel: PONTOS_POR_NIVEL,
          nivel: p.nivel,
        })}
        aria-label={traduzir('Progresso do nível')}
      >
        <span className="xp-cheio" aria-hidden="true" />
        <span className="xp-botao" aria-hidden="true" />
      </div>
    </div>
  );
}
